'use client';

import * as React from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { FormHelperText } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper, { PaperProps } from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { debounce } from '@mui/material/utils';
import parse from 'autosuggest-highlight/parse';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { SearchIcon } from 'components/icons/search';
import { useSitePreferences } from 'context/site-preferences';
import { getFinalLocation } from 'helpers/location';
import { STATE_ABBREVIATIONS } from 'helpers/location/constant';
import { LocationInfo, useLocationStore } from 'stores/location';
import { PlaceType } from './types';

const useEnhancedEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

function CustomPaper(props: PaperProps) {
  return <Paper {...props}>{props.children}</Paper>;
}

const fetch = debounce(
  async (
    request: { input: string; sessionToken: any },
    callback: (results?: readonly PlaceType[]) => void,
    onComplete?: () => void,
  ) => {
    try {
      const response = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: request.input,
        sessionToken: request.sessionToken,
        includedPrimaryTypes: ['(regions)'],
        includedRegionCodes: ['us', 'pr'],
      });

      const predictions: PlaceType[] =
        response?.suggestions?.map((s: any) => {
          const place = s.placePrediction;

          const secondary =
            place?.structuredFormat?.secondaryText?.text ??
            place?.text?.text?.split(',').slice(1).join(',').trim() ??
            '';

          return {
            place_id: place?.placeId,
            description: place?.text?.text ?? '',
            structured_formatting: {
              main_text: place?.structuredFormat?.mainText?.text ?? place?.text?.text ?? '',
              main_text_matched_substrings: [],
              secondary_text: secondary || place?.text?.text,
              secondary_text_matched_substrings: [],
            },
          };
        }) ?? [];

      if (predictions.length === 0) {
        callback(undefined);
      } else {
        callback(predictions);
      }
    } catch {
      onComplete?.();
    }
  },
  400,
);

const emptyOptions = [] as any;
let sessionToken: any;

type Props = {
  onChange?: (data: LocationInfo) => void;
  onCurrentLocationError?: (error: string) => void;
  errorMessage?: string;
  className?: string;
  onUpdateLocation?: (event?: React.FormEvent<HTMLFormElement> | undefined) => void;
};

export default function AutoCompleteGooglePlaces({
  onChange,
  onCurrentLocationError,
  errorMessage,
  className,
  onUpdateLocation,
}: Props) {
  const [value, setValue] = React.useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const { stateAbbreviations } = useSitePreferences();
  const stateValues = React.useMemo(() => {
    try {
      return JSON.parse(stateAbbreviations ?? '') ?? STATE_ABBREVIATIONS;
    } catch {
      return STATE_ABBREVIATIONS;
    }
  }, [stateAbbreviations]);

  const translate = useTranslations();
  const [chooseFirst, setChooseFirst] = React.useState(false);
  const hasError = !!errorMessage;
  const setOptions = useLocationStore((s) => s.setOptions);
  const options = useLocationStore((s) => s.options);
  const setOptionsLoading = useLocationStore((s) => s.setOptionsLoading);
  const setLocationInput = useLocationStore((s) => s.setLocationInput);

  useEnhancedEffect(() => {
    if (inputValue === '') {
      setOptions(value ? [value] : emptyOptions);
      return undefined;
    }

    // Allow to resolve the out of order request resolution.
    let active = true;
    setOptionsLoading(true);
    if (!sessionToken) {
      sessionToken = new google.maps.places.AutocompleteSessionToken();
    }
    fetch(
      { input: inputValue, sessionToken },
      (results?: readonly PlaceType[]) => {
        if (!active) {
          setOptionsLoading(false);
          return;
        }

        let newOptions: readonly PlaceType[] = [];

        if (results) {
          newOptions = results;

          if (value) {
            newOptions = [value, ...results.filter((result) => result.description !== value.description)];
          }
        } else if (value) {
          newOptions = [value];
        }
        setOptions(newOptions);

        if (chooseFirst) {
          handleChange(undefined, newOptions.at(0));
          setChooseFirst(false); //reset
        } else {
          setOptionsLoading?.(false);
        }
      },
      () => setOptionsLoading?.(false),
    );
    return () => {
      active = false;
      setOptionsLoading?.(false);
    };
  }, [value, inputValue, chooseFirst]);

  const handleChange = React.useCallback(
    async (event: any, newValue: any) => {
      event?.stopPropagation();
      const val = newValue && newValue?.place_id ? newValue : options.at(0);
      if (!val?.place_id) {
        if (event.key === 'Enter') {
          setChooseFirst(true);
        }
        return;
      }
      setOptions(val ? [val, ...options] : options);
      const finalLocation = await getFinalLocation(val, stateValues);

      setValue(val);
      onChange?.(finalLocation);
      setOptionsLoading(false);
    },
    [onChange, options, setOptions, setOptionsLoading, stateValues],
  );

  return (
    <div className={classNames('component--auto-complete-google-places', className)}>
      <Autocomplete
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        filterOptions={(x) => x}
        slots={{
          paper: CustomPaper,
        }}
        options={options}
        autoComplete
        includeInputInList
        filterSelectedOptions
        autoHighlight
        value={value}
        freeSolo={true}
        onChange={handleChange}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          setLocationInput(newInputValue);
          onCurrentLocationError?.('');
          setChooseFirst(false);
        }}
        className="w-full"
        renderInput={(params) => (
          <div
            ref={params.InputProps.ref}
            className={classNames(
              'flex w-full items-center rounded-full px-24 py-4',
              'bg-gray-0 text-primary',
              hasError ? 'border border-red-500' : 'border border-transparent',
            )}
          >
            <input
              {...params.inputProps}
              id="location-autocomplete"
              type="text"
              aria-invalid={hasError}
              aria-required={true}
              className={classNames(
                'global-text-md w-full bg-gray-0 outline-none',
                'focus:border-none focus:shadow-none focus:outline-none focus:ring-0',
                'rounded-full border-none',
                'text-[--color-secondary-main]',
                'text-[18px]',
                'sm:text-[16px]',
              )}
              placeholder={translate('location.city-zip-code')}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setChooseFirst(true);
                  onUpdateLocation?.(undefined);
                }
              }}
            />

            <SearchIcon />
          </div>
        )}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const matches = option.structured_formatting.main_text_matched_substrings;

          const parts = parse(
            option.structured_formatting.main_text,
            matches.map((match: any) => [match.offset, match.offset + match.length]),
          );
          return (
            <li
              key={key}
              {...optionProps}
              onMouseUp={(event) => {
                event.stopPropagation(); // ✅ prevent bubbling to document
              }}
            >
              <Grid container sx={{ alignItems: 'center' }}>
                <Grid sx={{ display: 'flex', width: 44 }}>
                  <LocationOnIcon sx={{ color: 'text.secondary' }} />
                </Grid>
                <Grid sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                  {parts.map((part, index) => (
                    <Box
                      key={index}
                      component="span"
                      sx={{
                        fontWeight: part.highlight ? 'fontWeightBold' : 'fontWeightRegular',
                      }}
                    >
                      {part.text}
                    </Box>
                  ))}
                  {option.structured_formatting.secondary_text ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {option.structured_formatting.secondary_text}
                    </Typography>
                  ) : null}
                </Grid>
              </Grid>
            </li>
          );
        }}
      />
      {hasError && (
        <FormHelperText error role="alert" style={{ marginLeft: 14 }}>
          {errorMessage}
        </FormHelperText>
      )}
    </div>
  );
}
